import { ImageProps } from 'next/image';
import { MouseEventHandler } from 'react';
import { useIntl } from 'react-intl';

import { ampli } from '../../../core/Tracking/Amplitude/__AUTO_GENERATED__';
import {
    TrackingProperties,
    useTrackEventAmplitude,
} from '../../../core/Tracking/Amplitude/Amplitude';
import { TravelGuideArticleCard as TravelGuideArticleDesignSystem } from '../../../design-system/components/card/travelGuideArticle/TravelGuideArticleCard';
import { DestinationArticle } from '../../../resources/destinationArticles/domain/types/destinationArticle';
import { useUrlGenerator } from '../../../shared/hooks/useUrlGenerator';
import { PickDomain } from '../../../shared/utilities/types/PickDomain';
import { imageLoaderForId } from '../../Layout/Image/URL/ImageURL';

export type DestinationArticleCardProps = {
    article: PickDomain<
        DestinationArticle,
        'documentId' | 'slug' | 'title' | 'summary' | 'type',
        {
            coverPicture: DestinationArticle['coverPicture'] | null;
            destination: PickDomain<
                DestinationArticle['destination'],
                'id' | 'slug' | 'coverPicture'
            >;
        }
    >;
    image: Partial<Omit<ImageProps, 'alt' | 'src' | 'loader' | 'sizes'>> & {
        sizes: ImageProps['sizes'];
    };
    trackingInformation: {
        click: Omit<
            TrackingProperties<typeof ampli.destinationArticleClicked>,
            'article_type' | 'destination_id' | 'destination_slug'
        >;
    };
};

export function DestinationArticleCard({
    article,
    image,
    trackingInformation,
}: DestinationArticleCardProps) {
    const { formatMessage } = useIntl();
    const { generatePath } = useUrlGenerator();
    const { trackEvent } = useTrackEventAmplitude();

    const articleRouteName =
        article.type === 'general_inspiration'
            ? 'destination_inspiration_article'
            : 'destination_practical_information_article';

    const articleUrl = generatePath(articleRouteName, {
        destinationSlug: article.destination.slug,
        articleDocumentId: article.documentId,
        articleSlug: article.slug,
    });

    const handleClick: MouseEventHandler<HTMLAnchorElement> = () => {
        trackEvent((ampli, defaultProperties) => {
            ampli.destinationArticleClicked({
                ...defaultProperties,
                article_type: article.type,
                destination_id: article.destination.id,
                destination_slug: article.destination.slug,
                ...trackingInformation.click,
            });
        });
    };

    return (
        <TravelGuideArticleDesignSystem
            title={article.title}
            summary={article.summary}
            articleUrl={articleUrl}
            onClick={handleClick}
            image={{
                ...image,
                alt: '',
                src: article.coverPicture
                    ? article.coverPicture.id
                    : article.destination.coverPicture.id,
                loader: imageLoaderForId({
                    aspectRatio: TravelGuideArticleDesignSystem.PICTURE_RATIO,
                }),
            }}
            ctaLabel={formatMessage({ id: 'global.cta.continueReading' })}
        />
    );
}
